import { useEffect, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { apiClient } from '../../../api';
import type { TrackerRow } from './types';
import { CAT_LABEL } from './types';

const CATS = ['all', '0', '1', '2', '3'] as const;

function RagDot({ rag }: { rag: string | null }) {
  if (!rag) return <span className="text-gray-300">—</span>;
  const color = rag === 'green' ? 'bg-emerald-500' : rag === 'yellow' ? 'bg-amber-400' : rag === 'red' ? 'bg-red-500' : 'bg-gray-300';
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block w-2 h-2 rounded-full ${color}`} />
      <span className="text-gray-500">{rag}</span>
    </span>
  );
}

export default function CategoryTracker({ refreshKey }: { refreshKey: number }) {
  const [cat, setCat] = useState<(typeof CATS)[number]>('all');
  const [track, setTrack] = useState<'all' | 'Workflow' | 'Reporting'>('all');
  const [items, setItems] = useState<TrackerRow[]>([]);
  const [saving, setSaving] = useState<number | null>(null);
  const [help, setHelp] = useState<Record<string, { title: string; body: string }>>({});

  useEffect(() => {
    apiClient.get('/api/cloud-portfolio/catalog')
      .then((r) => setHelp(r.data.operating_model || {}))
      .catch(() => setHelp({}));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const params: Record<string, string> = {};
    if (track !== 'all') params.track = track;
    apiClient.get(`/api/cloud-portfolio/categories/${cat}/tracker`, { params })
      .then((r) => { if (!cancelled) setItems(r.data.items || []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [cat, track, refreshKey]);

  const patch = async (id: number, field: keyof TrackerRow, value: string) => {
    setSaving(id);
    try {
      const res = await apiClient.patch(`/api/cloud-portfolio/tracker/${id}`, { [field]: value });
      setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...res.data } : r)));
    } finally {
      setSaving(null);
    }
  };

  const extraPatch = async (id: number, extra: Record<string, string>) => {
    setSaving(id);
    try {
      const res = await apiClient.patch(`/api/cloud-portfolio/tracker/${id}`, { extra });
      setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...res.data } : r)));
    } finally {
      setSaving(null);
    }
  };

  const showCat0 = cat === '0' || cat === 'all';
  const showCat1 = cat === '1' || cat === 'all';
  const showCat2Wf = cat === '2' || cat === 'all';
  const showCat2Rep = cat === '2' || cat === 'all';

  const columns = useMemo(() => {
    const base: { key: string; label: string }[] = [
      { key: 'category', label: 'Cat' },
      { key: 'name', label: 'Application / Item' },
      { key: 'eai_nbr', label: 'EAI' },
      { key: 'owner', label: 'Owner' },
      { key: 'current_status', label: 'Current Status' },
    ];
    if (showCat0) {
      base.push({ key: 'funding', label: 'Funding' });
    }
    if (showCat1) {
      base.push({ key: 'dev_status', label: 'Dev status' }, { key: 'prod_status', label: 'Prod status' });
    }
    if (showCat2Wf) {
      base.push({ key: 'poa_decision', label: 'POA / landing' }, { key: 'meeting_group', label: 'Meeting group' });
    }
    if (showCat2Rep) {
      base.push({ key: 'app_count', label: 'App count' });
    }
    base.push(
      { key: 'gfd_required', label: 'GFD' },
      { key: 'gfd_rag', label: 'GFD RAG' },
      { key: 'blockers', label: 'Blockers' },
      { key: 'complete', label: 'Complete?' },
      { key: 'notes', label: 'Notes' },
    );
    return base;
  }, [showCat0, showCat1, showCat2Wf, showCat2Rep]);

  const filtered = items.filter((r) => {
    if (cat === '2' && track !== 'all' && r.category_track !== track) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {CATS.map((c) => (
          <button
            key={c}
            onClick={() => { setCat(c); if (c !== '2') setTrack('all'); }}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold ${
              cat === c ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {c === 'all' ? 'All' : CAT_LABEL[Number(c)]}
          </button>
        ))}
        {cat === '2' && (
          <div className="flex gap-1 ml-2">
            {(['all', 'Workflow', 'Reporting'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTrack(t)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                  track === t ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500'
                }`}
              >
                {t === 'all' ? 'Both tracks' : t}
              </button>
            ))}
          </div>
        )}
      </div>

      {help.categories && (
        <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span><b className="text-gray-600">{help.categories.title}.</b> {help.categories.body}</span>
        </div>
      )}
      {help.complete && (
        <p className="text-[11px] text-gray-400">{help.complete.body}</p>
      )}

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="bg-gray-50 text-gray-500 font-semibold text-left px-2 py-2 border-b border-gray-200 whitespace-nowrap">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className={`even:bg-gray-50 ${r.hidden_in_source ? '' : ''}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {r.category == null ? '—' : r.category}
                    {r.category_track ? ` · ${r.category_track}` : ''}
                    {r.hidden_in_source && <span className="ml-1 text-[10px] text-gray-400">hid</span>}
                  </td>
                  <td className="px-2 py-1.5 min-w-[160px] font-medium text-gray-800">{r.name}</td>
                  <td className="px-2 py-1.5">{r.eai_nbr ?? (r.entity_type === 'application' ? '—' : r.entity_type)}</td>
                  <EditableCell value={r.owner} saving={saving === r.id} onSave={(v) => patch(r.id, 'owner', v)} />
                  <EditableCell value={r.current_status} saving={saving === r.id} onSave={(v) => patch(r.id, 'current_status', v)} wide />
                  {showCat0 && (
                    <EditableCell value={r.funding} saving={saving === r.id} onSave={(v) => patch(r.id, 'funding', v)} />
                  )}
                  {showCat1 && (
                    <>
                      <EditableCell
                        value={String((r.extra as Record<string, string>)?.dev_status || '')}
                        saving={saving === r.id}
                        onSave={(v) => extraPatch(r.id, { dev_status: v })}
                      />
                      <EditableCell
                        value={String((r.extra as Record<string, string>)?.prod_status || '')}
                        saving={saving === r.id}
                        onSave={(v) => extraPatch(r.id, { prod_status: v })}
                      />
                    </>
                  )}
                  {showCat2Wf && (
                    <>
                      <EditableCell value={r.poa_decision} saving={saving === r.id} onSave={(v) => patch(r.id, 'poa_decision', v)} />
                      <EditableCell
                        value={String((r.extra as Record<string, string>)?.meeting_group || r.subgroup || '')}
                        saving={saving === r.id}
                        onSave={(v) => extraPatch(r.id, { meeting_group: v })}
                      />
                    </>
                  )}
                  {showCat2Rep && (
                    <td className="px-2 py-1.5 text-gray-600">{r.app_count ?? '—'}</td>
                  )}
                  <EditableCell value={r.gfd_required} saving={saving === r.id} onSave={(v) => patch(r.id, 'gfd_required', v)} />
                  <td className="px-2 py-1.5"><RagDot rag={r.gfd_rag} /></td>
                  <EditableCell value={r.blockers} saving={saving === r.id} onSave={(v) => patch(r.id, 'blockers', v)} wide />
                  <EditableCell value={r.complete} saving={saving === r.id} onSave={(v) => patch(r.id, 'complete', v)} />
                  <EditableCell value={r.notes} saving={saving === r.id} onSave={(v) => patch(r.id, 'notes', v)} wide />
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-gray-400">No tracker rows. Import the Phase 3 workbook (hidden Cat 1–3 rows included).</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function EditableCell({
  value, onSave, saving, wide,
}: {
  value: string | null | undefined;
  onSave: (v: string) => void;
  saving: boolean;
  wide?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);
  if (!editing) {
    return (
      <td
        className={`px-2 py-1.5 cursor-text ${wide ? 'min-w-[140px]' : 'min-w-[90px]'} ${saving ? 'opacity-50' : ''}`}
        onClick={() => setEditing(true)}
        title="Click to edit"
      >
        {value || <span className="text-gray-300">—</span>}
      </td>
    );
  }
  return (
    <td className="px-1 py-1">
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { setEditing(false); if (draft !== (value || '')) onSave(draft); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); }
          if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
        }}
        className="w-full text-xs border border-amber-300 rounded px-1.5 py-1 focus:outline-none"
      />
    </td>
  );
}
